// regras/contratos.js — a operação central do negócio, fora da interface.
//
// Por que este arquivo existe: criar um aluno com contrato é a coisa mais importante
// que o professor faz. Hoje isso acontece dentro de uma tela React — só funciona se
// alguém estiver com o navegador aberto. O WhatsApp não abre navegador; um lembrete
// automático das 8h também não. Aqui a regra recebe o cliente do banco por parâmetro,
// então o mesmo código serve ao painel de hoje e ao canal de voz de amanhã.
//
// Dois modos:
//   'rascunho'  → não grava nada; devolve resumo, o que falta e os avisos.
//                 É o "confirmar em 1 toque antes de valer" do slide 6 do deck.
//   'aplicar'   → grava aluno + contrato + agenda + aulas + créditos + cobrança.

import { recalcularCreditos } from './creditos.js';

const DIAS_LABEL = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

// Dias sugeridos por frequência — nunca "flexível" por padrão.
export function diasPadrao(freq) {
  const mapa = { 1: [1], 2: [1, 3], 3: [1, 3, 5], 4: [1, 2, 4, 5], 5: [1, 2, 3, 4, 5] };
  return mapa[freq] || [1];
}

function pad2(n) { return String(n).padStart(2, '0'); }
function fmtData(d) { return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`; }
// v155: data e hora de agora em Brasilia (UTC-3, sem horario de verao desde 2019 — a mesma conta do banco)
function agoraSP() { const s = new Date(Date.now() - 3 * 3600000).toISOString(); return { data: s.slice(0, 10), hora: s.slice(11, 16) }; }
function diaMes(s) { return String(s || '').slice(8, 10) + '/' + String(s || '').slice(5, 7); }

function dataRazoavel(s) {
  if (!s || !/^\d{4}-\d{2}-\d{2}$/.test(String(s))) return false;
  const ano = Number(String(s).slice(0, 4));
  return ano >= 2020 && ano <= 2035;
}

// Normaliza a entrada — aceita tanto o que a tela manda quanto o que a voz vai mandar.
export function normalizar(dados = {}) {
  // Dois jeitos de cobrar, e sao mundos diferentes:
  //
  //   'pacote'  — compra 8 aulas, acaba, renova. Falta consome aula. Tem reposicao.
  //   'mensal'  — paga todo mes no dia do aniversario da entrada, tendo treinado ou nao.
  //               Agenda fixa, sem reposicao, sem saldo de aulas. Receita recorrente.
  //
  // Nao e' caracteristica de turma: existe personal que cobra mensalidade de aluno
  // individual e professor de turma que cobra aula avulsa. Sao eixos independentes.
  const mensal = dados.tipo_cobranca === 'mensal';

  const valorAula = Number(dados.valor_aula) || 0;
  const dias = Array.isArray(dados.dias) && dados.dias.length
    ? [...new Set(dados.dias.map(Number))].sort()
    : (dados.aulas_por_semana ? diasPadrao(Number(dados.aulas_por_semana)) : []);
  // No mensal nao existe tamanho de pacote. Guardamos 0 para nenhuma tela mostrar saldo.
  const pacote = mensal ? 0 : (Number(dados.pacote) || (dias.length ? dias.length * 4 : 0));
  const emCurso = mensal ? false : !!dados.em_curso;
  const feitas = emCurso ? Math.min(Number(dados.aulas_feitas) || 0, pacote) : 0;
  const valorMensal = mensal ? (Number(dados.valor_mensal) || 0) : 0;

  // Um horario por dia da semana. O caso comum e' o mesmo horario em todos os dias,
  // mas "terca ao meio-dia e quinta as 19h" e' rotina de quadra — o aluno encaixa o
  // treino na agenda dele, nao numa grade fixa. Guardamos {2:'12:00', 4:'19:00'} e
  // mantemos `hora` como o horario do primeiro dia, para quem so' precisa de um.
  const horaValida = h => /^\d{1,2}:\d{2}/.test(String(h || '')) ? String(h).slice(0, 5).padStart(5, '0') : null;
  const horaBase = horaValida(dados.hora) || '08:00';
  const horarios = {};
  for (const dia of dias) {
    const bruto = dados.horarios ? (dados.horarios[dia] ?? dados.horarios[String(dia)]) : null;
    horarios[dia] = horaValida(bruto) || horaBase;
  }

  return {
    tipo_cobranca: mensal ? 'mensal' : 'pacote',
    valor_mensal: valorMensal,
    professor_id: dados.professor_id || null,
    plano_id: dados.plano_id || null,
    nome: (dados.nome || '').trim(),
    telefone: (dados.telefone || '').trim(),
    email: (dados.email || '').trim(),
    modalidade: dados.modalidade || '',
    custo_sala: Number(dados.custo_sala) || 0,   // custo do professor por aula (quadra/academia) — interno, nao vai no contrato do aluno
    valor_aula: valorAula,
    dias,
    hora: dias.length ? horarios[dias[0]] : horaBase,
    horarios,
    pacote,
    duracao: Number(dados.duracao) > 0 ? Number(dados.duracao) : 60,   // minutos por aula
    preco_total: Number(dados.preco_total) || 0,   // total exato digitado pelo professor (modo 'pelo pacote')
    aulas_por_semana: dias.length || Number(dados.aulas_por_semana) || 1,
    antecedencia: Number(dados.antecedencia) || 24,
    // v144: desmarcar LIVRE (sem prazo combinado): avisou antes da aula, nao conta. So' vai ao banco quando
    // alguem disse (true/false); sem dizer, fica o padrao do banco (false) — quem chama de outro lugar nao muda.
    cancelamento_livre: typeof dados.cancelamento_livre === 'boolean' ? dados.cancelamento_livre : undefined,
    reposicao: dados.reposicao !== false,
    reajuste: Number(dados.reajuste) || 0,
    em_curso: emCurso,
    aulas_feitas: feitas,
    pacote_pago: emCurso ? dados.pacote_pago !== false : false,
    // No mensal a reposicao nao existe: horario fixo, faltou perdeu, a mensalidade corre.
    reposicao_efetiva: mensal ? false : (dados.reposicao !== false),
    data_inicio: dados.data_inicio || fmtData(new Date()),
    // Independente do inicio do ciclo. O aluno pode fazer duas aulas do pacote novo na
    // terca e na quinta e so' pagar na sexta — ou pagar na sexta para comecar na segunda.
    // Uma data manda no calendario e nos creditos; a outra, no financeiro e na renovacao.
    data_pagamento: dados.data_pagamento || null,
    // Mensal: dia do vencimento escolhido pelo professor (1..31) e se a mensalidade do mes
    // corrente ja' foi paga. Sem isso o app assumia "vence hoje, em aberto" (Marcus, 25/09).
    dia_vencimento: mensal && Number(dados.dia_vencimento) >= 1 ? Math.min(31, Math.floor(Number(dados.dia_vencimento))) : null,
    mensal_mes_pago: mensal ? dados.mensal_mes_pago === true : false,
    // Presente so' quando isto e' um PACOTE NOVO para um aluno que ja' existe (renovacao) —
    // nesse caso nao se cria aluno nem agenda_semanal de novo, so' o contrato seguinte.
    aluno_id_existente: dados.aluno_id_existente || null,
    // v149: renovacao com aula do pacote atual ainda marcada — o pacote novo entra NA FILA (status 'proximo'),
    // comeca em data_inicio (o dia seguinte a' ultima aula marcada) e o atual termina as aulas dele.
    depois_do_atual: !!(dados.aluno_id_existente && dados.depois_do_atual),
    // v155: pacote novo de quem ja' tinha acordo com o professor (renovacao). O "Completar" (ficha sem acordo) nao e'.
    renovacao: !!(dados.aluno_id_existente && dados.renovacao),
    // v152: pacote na fila — a cobranca vence no dia da ultima aula marcada do pacote ATUAL (o app manda essa data).
    vencimento_fila: dados.aluno_id_existente && dados.depois_do_atual && dataRazoavel(dados.vencimento_fila) ? dados.vencimento_fila : null,
  };
}

// O que ainda falta para o contrato poder valer. A camada de voz usa esta lista
// como o roteiro de perguntas — ela não precisa conhecer as regras do negócio.
export function faltando(d) {
  const mensal = d.tipo_cobranca === 'mensal';
  const faltas = [];
  if (!d.professor_id) faltas.push({ campo: 'professor_id', pergunta: 'Qual professor?' });
  if (!d.nome) faltas.push({ campo: 'nome', pergunta: 'Como você chama esse aluno?' });
  if (!d.dias.length) faltas.push({ campo: 'dias', pergunta: 'Quais dias da semana ele treina?' });

  if (mensal) {
    if (!d.valor_mensal || d.valor_mensal <= 0) faltas.push({ campo: 'valor_mensal', pergunta: 'Quanto ele paga por mês?' });
    if (!dataRazoavel(d.data_inicio)) faltas.push({ campo: 'data_inicio', pergunta: 'Desde quando ele treina com você?' });
    return faltas;   // no mensal nao existe tamanho de pacote nem "ja' pagou o pacote"
  }

  if (!d.valor_aula || d.valor_aula <= 0) faltas.push({ campo: 'valor_aula', pergunta: 'Quanto você cobra por aula?' });
  if (!d.pacote || d.pacote <= 0) faltas.push({ campo: 'pacote', pergunta: 'Quantas aulas tem o pacote?' });
  if (d.pacote > 200) faltas.push({ campo: 'pacote', pergunta: `${d.pacote} aulas? Confira a quantidade — o máximo é 200 por pacote.` });
  if (d.em_curso && d.pacote_pago === undefined) faltas.push({ campo: 'pacote_pago', pergunta: 'Esse pacote já está pago?' });
  if (!dataRazoavel(d.data_inicio)) faltas.push({ campo: 'data_inicio', pergunta: 'Quando o pacote começou?' });
  return faltas;
}

// Dia do mes em que a mensalidade vence — o mesmo dia em que o aluno entrou.
// Dia 31 em mes de 30 cai no ultimo dia: quem entra em 31/01 vence em 28/02.
export function diaVencimento(d) {
  if (d.dia_vencimento) return d.dia_vencimento;
  const inicio = new Date(d.data_inicio + 'T12:00:00');
  return isNaN(inicio) ? 1 : inicio.getDate();
}

export function calcular(d) {
  const inicio = new Date(d.data_inicio + 'T12:00:00');

  if (d.tipo_cobranca === 'mensal') {
    // Nao existe "fim": e' recorrente ate' alguem encerrar. Geramos agenda numa janela
    // movel (90 dias) e a rotina diaria do banco vai repondo conforme o tempo passa.
    const fim = new Date(inicio > new Date() ? inicio : new Date());
    fim.setDate(fim.getDate() + JANELA_AGENDA_DIAS);
    const custoMes = d.custo_sala * d.dias.length * 4.33;   // 4,33 semanas no mes, em media
    return {
      restantes: null,                          // mensal nao tem saldo de aulas
      preco_pacote: d.valor_mensal,             // o "pacote" do mensal e' um mes
      custo_pacote: Math.round(custoMes),
      liquido_pacote: Math.round(d.valor_mensal - custoMes),
      data_fim: fmtData(fim),
      dia_vencimento: diaVencimento(d),
      gera_cobranca: true,                      // a 1a mensalidade nasce junto com o contrato
    };
  }

  const restantes = Math.max(0, d.pacote - d.aulas_feitas);
  const porSemana = Math.max(1, d.dias.length);
  const semanas = Math.max(1, Math.ceil(restantes / porSemana));
  // As aulas que faltam comecam a ser geradas HOJE quando o ciclo ja' vinha rolando
  // (ver gerarAulas). O fim tem de contar a partir do mesmo ponto, senao ele nasce no
  // passado, a janela fecha antes de abrir e nenhuma aula e' criada.
  const hojeCalc = new Date(); hojeCalc.setHours(12, 0, 0, 0);
  const base = (d.aulas_feitas > 0 && inicio < hojeCalc) ? hojeCalc : inicio;
  const fim = new Date(base);
  // +1 semana de folga: se um feriado cair no meio, a ultima aula escorrega para
  // depois do fim "exato" e a janela de geracao nao pode fechar antes dela.
  fim.setDate(fim.getDate() + semanas * 7 + 7);
  const precoPacote = d.preco_total > 0 ? d.preco_total : d.valor_aula * d.pacote;
  const custoPacote = d.custo_sala * d.pacote;
  return {
    restantes,
    preco_pacote: precoPacote,               // pacote é binário: paga inteiro ou deve inteiro
    custo_pacote: custoPacote,               // quanto o professor gasta de sala neste pacote
    liquido_pacote: precoPacote - custoPacote,
    data_fim: fmtData(fim),
    dia_vencimento: null,
    gera_cobranca: !(d.em_curso && d.pacote_pago),
  };
}

// Quantos dias de agenda o mensal mantem preenchidos à frente.
export const JANELA_AGENDA_DIAS = 90;

// Horário daquele dia da semana — cai no horário geral se o dia não tem um próprio.
export function horaDoDia(d, dia) {
  return (d.horarios && d.horarios[dia]) || d.hora;
}

// "Ter · Qui às 12:00" quando é tudo no mesmo horário; "Ter 12:00 · Qui 19:00" quando não é.
// A frase curta é a que o professor lê no resumo e a que o aluno vê no contrato, então
// ela precisa dizer a verdade sem virar uma tabela.
export function descreverAgenda(d) {
  if (!d.dias || !d.dias.length) return 'dias a definir';
  const horas = d.dias.map(i => horaDoDia(d, i));
  const todasIguais = horas.every(h => h === horas[0]);
  if (todasIguais) return `${d.dias.map(i => DIAS_LABEL[i]).join(' · ')} às ${horas[0]}`;
  return d.dias.map((i, k) => `${DIAS_LABEL[i]} ${horas[k]}`).join(' · ');
}

// Texto que o professor confirma em um toque (e que a voz vai ler de volta).
export function resumo(d, c) {
  if (d.tipo_cobranca === 'mensal') {
    const linhas = [
      `${d.nome || 'Aluno'} — ${descreverAgenda(d)}`,
      `Mensalidade de R$ ${d.valor_mensal.toFixed(0)} · vence todo dia ${c.dia_vencimento}`,
      'Horário fixo, sem reposição — a mensalidade corre tendo treinado ou não',
      d.reajuste ? `Reajuste de ${d.reajuste}% ao ano` : 'Sem reajuste previsto',
    ];
    if (d.custo_sala > 0) linhas.push(`Sala: R$ ${d.custo_sala.toFixed(0)}/aula — seu líquido no mês: R$ ${c.liquido_pacote.toFixed(0)}`);
    linhas.push(`Primeira mensalidade de R$ ${d.valor_mensal.toFixed(0)} ficará em aberto`);
    return linhas.join('\n');
  }

  const linhas = [
    `${d.nome || 'Aluno'} — ${descreverAgenda(d)}`,
    `Pacote de ${d.pacote} aulas · R$ ${d.valor_aula.toFixed(0)}/aula · total R$ ${c.preco_pacote.toFixed(0)}`,
    `${d.cancelamento_livre ? 'Desmarcar livre (sem prazo combinado)' : `Cancelamento com ${d.antecedencia}h`}${d.reposicao ? ' — com reposição' : ' — sem reposição'}`,
    d.reajuste ? `Reajuste de ${d.reajuste}% ao ano` : 'Sem reajuste previsto',
  ];
  if (d.custo_sala > 0) linhas.push(`Sala: R$ ${d.custo_sala.toFixed(0)}/aula — seu líquido no pacote: R$ ${c.liquido_pacote.toFixed(0)}`);
  if (d.em_curso) linhas.push(`${d.aulas_feitas} de ${d.pacote} já realizadas · ${c.restantes} restantes`);
  linhas.push(c.gera_cobranca ? `Cobrança de R$ ${c.preco_pacote.toFixed(0)} ficará em aberto` : 'Pacote já pago — sem cobrança');
  return linhas.join('\n');
}

// Coisas que não impedem de gravar, mas o professor precisa saber antes de confirmar.
// Evita o aluno duplicado quando o mesmo áudio é enviado duas vezes no WhatsApp.
export async function avisos(db, d) {
  const lista = [];
  if (!d.professor_id || !d.nome) return lista;
  const { data } = await db.from('alunos')
    .select('id, nome')
    .eq('professor_id', d.professor_id)
    .ilike('nome', `%${d.nome}%`);
  if (data && data.length) lista.push(`Já existe "${data[0].nome}" na sua lista — confira se não é a mesma pessoa.`);
  if (d.pacote && d.dias.length && d.pacote % d.dias.length !== 0) {
    lista.push('O pacote não fecha em semanas exatas — a última semana terá menos aulas.');
  }
  return lista;
}

// Datas que nao existem na grade: feriados nacionais + os que o professor cadastrou.
// Vem de fora (a tela busca no banco) para este modulo continuar puro e testavel.
function gerarAulas(d, c, ids, feriados = new Set(), ocupadas = new Set()) {
  const aulas = [];
  // v152: dia+horario em que o aluno ja' tem aula (ex.: renovou hoje depois da ultima aula do pacote antigo, no mesmo
  // horario) nao ganha outra — a aula vai para a proxima ocorrencia (caso Adriana, 29/10: 3 aulas qui 09:00).
  const livre = dt => !ocupadas.has(fmtData(dt) + '|' + String(horaDoDia(d, dt.getDay()) || '').slice(0, 5));
  const hoje = new Date(); hoje.setHours(12, 0, 0, 0);
  const inicio = new Date(d.data_inicio + 'T12:00:00');

  // Pacote que "ja' comecou, N feitas": as N aulas passadas entram na agenda como
  // REALIZADAS, com data — para o professor conferir e o aluno ver o historico.
  // Antes elas viravam so' um numero ("anteriores") e o aluno aparecia com "0 aulas
  // realizadas" e agenda vazia (relato do Cadu, 22/09). Se o professor deu a data de
  // inicio, as datas andam para a frente a partir dela; senao, andam para tras a partir
  // de ontem, pelos dias que ele treina. Feriado nao conta em nenhum dos dois casos.
  const feitasN = Math.max(0, Number(d.aulas_feitas) || 0);
  if (feitasN > 0) {
    const ontem = new Date(hoje); ontem.setDate(ontem.getDate() - 1);
    const passadas = [];
    const cabe = dt => d.dias.includes(dt.getDay()) && !feriados.has(fmtData(dt)) && livre(dt);
    if (inicio < hoje) {
      const cur = new Date(inicio); let g = 0;
      while (cur <= ontem && passadas.length < feitasN && g++ < 800) { if (cabe(cur)) passadas.push(new Date(cur)); cur.setDate(cur.getDate() + 1); }
    }
    if (passadas.length < feitasN) {
      // faltou: completa para tras, a partir do dia anterior a primeira ja' colocada (ou de ontem)
      const cur = new Date(passadas.length ? passadas[0] : new Date(ontem.getTime() + 86400000)); cur.setDate(cur.getDate() - 1); let g = 0;
      while (passadas.length < feitasN && g++ < 800) { if (cabe(cur)) passadas.unshift(new Date(cur)); cur.setDate(cur.getDate() - 1); }
    }
    passadas.forEach(dt => aulas.push({
      aluno_id: ids.aluno_id, professor_id: d.professor_id, contrato_id: ids.contrato_id,
      data: fmtData(dt), hora: horaDoDia(d, dt.getDay()), status: 'realizada', consome_credito: true,
    }));
  }

  // As restantes comecam HOJE (nao da data de inicio): as do passado ja' entraram acima.
  const cursor = (feitasN > 0 && inicio < hoje) ? hoje : inicio;
  const fim = new Date(c.data_fim + 'T12:00:00');
  // No mensal nao existe numero de aulas para acabar: o limite e' a janela de tempo.
  // A rotina diaria do banco repoe o que for saindo, para a agenda nunca esvaziar.
  const teto = c.restantes === null ? 999999 : c.restantes;
  // v155: aula de HOJE cujo horario ja' passou nao nasce (renovou as 15h, treino as 9h): vai para a proxima ocorrencia
  const ag = agoraSP();
  const jaPassou = dt => { const h = String(horaDoDia(d, dt.getDay()) || '').slice(0, 5); return fmtData(dt) === ag.data && !!h && h <= ag.hora; };
  let guarda = 0, futuras = 0;
  while (cursor <= fim && futuras < teto && guarda++ < 800) {
    // Feriado nao tem aula — como em academia e escola, o dia simplesmente nao existe na
    // grade. A aula vai para a proxima ocorrencia; o pacote continua com o mesmo tamanho.
    if (d.dias.includes(cursor.getDay()) && !feriados.has(fmtData(cursor)) && livre(cursor) && !jaPassou(cursor)) {
      aulas.push({
        aluno_id: ids.aluno_id, professor_id: d.professor_id, contrato_id: ids.contrato_id,
        data: fmtData(cursor), hora: horaDoDia(d, cursor.getDay()), status: 'agendada', consome_credito: false,
      });
      futuras++;
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  return aulas;
}

// Regras que valem para todos os alunos do professor. Perguntadas uma vez no primeiro
// acesso e reaproveitadas em cada contrato — inclusive pela voz, que assim não precisa
// perguntar cancelamento e reajuste em todo áudio.
export const PADROES_FALLBACK = { antecedencia: 24, reposicao: true, reajuste: 0 };

export async function carregarPadroes(db, professorId) {
  if (!professorId) return { ...PADROES_FALLBACK };
  const { data, error } = await db.from('professores')
    .select('padrao_antecedencia_horas, padrao_reposicao, padrao_reajuste_pct')
    .eq('id', professorId).maybeSingle();
  if (error || !data) return { ...PADROES_FALLBACK };
  return {
    antecedencia: data.padrao_antecedencia_horas ?? PADROES_FALLBACK.antecedencia,
    reposicao: data.padrao_reposicao ?? PADROES_FALLBACK.reposicao,
    reajuste: data.padrao_reajuste_pct ?? PADROES_FALLBACK.reajuste,
  };
}

// A linha do acordo (sem aluno e professor) — a mesma nos dois caminhos: o de sempre (o app grava) e o
// pedido do aluno que ja' tem conta (v144: o banco grava, professor_completar_pedido).
function linhaContrato(d, c) {
  return {
    plano_id: d.plano_id,
    preco_negociado: c.preco_pacote,
    data_inicio: d.data_inicio, data_fim: c.data_fim,
    status: d.depois_do_atual ? 'proximo' : 'ativo', modo: 'interno', status_aceite: 'pendente',
    total_aulas: d.pacote, aulas_por_semana: d.aulas_por_semana,
    aulas_realizadas_anterior: 0,   // as feitas viram linhas 'realizada' na agenda (ver gerarAulas)
    duracao_min: d.duracao,
    tipo_cobranca: d.tipo_cobranca,
    valor_mensal: d.valor_mensal || null,
    dia_vencimento: c.dia_vencimento,
    antecedencia_cancelamento_horas: d.antecedencia,
    ...(typeof d.cancelamento_livre === 'boolean' ? { cancelamento_livre: d.cancelamento_livre } : {}),
    reposicao_permitida: d.reposicao_efetiva,
    limite_reposicao: d.reposicao_efetiva ? 1 : 0,
    reajuste_percentual: d.reajuste,
    taxa_academia: d.custo_sala,
  };
}

// A primeira cobranca do acordo (sem aluno, professor e acordo) e o nome dela nos avisos de problema.
function linhaPagamentoInicial(d, c, entrada) {
  if (d.tipo_cobranca === 'mensal') {
    // A mensalidade do mes corrente vence no dia escolhido (neste mes). Se o professor
    // disse que ja' esta' paga, entra como paga — e a rotina do banco (rodar_mensalidades)
    // so' cria a do mes seguinte, no mesmo dia. Dia 31 em mes curto cai no ultimo dia.
    const ini = new Date(d.data_inicio + 'T12:00:00');
    const ultimo = new Date(ini.getFullYear(), ini.getMonth() + 1, 0).getDate();
    const venc = new Date(ini.getFullYear(), ini.getMonth(), Math.min(c.dia_vencimento, ultimo), 12);
    const pagoJa = d.mensal_mes_pago;
    return { rotulo: 'mensalidade', linha: {
      valor: d.valor_mensal,
      tipo: pagoJa ? 'migracao' : 'mensalidade', status: pagoJa ? 'pago' : 'pendente',
      // Etapa 3: quem deu a baixa = 'migracao' (pago antes do cadastro). Sem meio: ninguem disse como.
      confirmado_via: pagoJa ? 'migracao' : null,
      referencia_mes: d.data_inicio.slice(0, 7),
      data_pagamento: pagoJa && d.data_pagamento ? d.data_pagamento + 'T12:00:00' : null,
      data_vencimento: fmtData(venc),
    } };
  }
  if (c.gera_cobranca) {
    // Cobranca nasce em aberto, com vencimento — inadimplencia so' depois do prazo.
    const venc = new Date(d.data_inicio + 'T12:00:00');
    venc.setDate(venc.getDate() + (Number(entrada.prazo_pagamento_dias) || 5));
    return { rotulo: 'cobrança', linha: {
      valor: c.preco_pacote,
      tipo: 'aguardando', status: 'pendente',
      referencia_mes: d.data_inicio.slice(0, 7), data_pagamento: null,
      data_vencimento: fmtData(venc),
    } };
  }
  // Pacote migrado que JA' estava pago: registra o pagamento para o historico ficar
  // completo (o aluno via "Pago no pacote: R$ 0" e parecia devedor). Marcado como
  // 'migracao' para nao inflar o faturamento do mes do professor.
  return { rotulo: 'registro do pacote pago', linha: {
    valor: c.preco_pacote,
    tipo: 'migracao', status: 'pago', confirmado_via: 'migracao',
    referencia_mes: (d.data_pagamento || d.data_inicio).slice(0, 7),
    // Sem data informada fica em branco — o app pede ao professor para confirmar.
    data_pagamento: d.data_pagamento ? d.data_pagamento + 'T12:00:00' : null,
    data_vencimento: d.data_pagamento || d.data_inicio,
  } };
}

/**
 * v144 — pedido de um aluno que JA' USA o app (Marcus 21/10: "eu mandei o convite pro Iago todo preenchido e
 * voltou para mim um convite dele"). O professor nao cria ficha nova nem manda convite de volta: o banco adota
 * a ficha do aluno e grava nela acordo, agenda, aulas, creditos e a 1a cobranca, tudo de uma vez
 * (professor_completar_pedido, sql/v144_pedido.sql). As contas sao as de sempre (normalizar, calcular,
 * gerarAulas); o banco confere e compara com o que o aluno mandou: igual -> o acordo ja' nasce aceito.
 * Devolve { ok, aluno_id, contrato_id, aceito, mudancas, aulas_criadas } ou { ok:false, codigo | erro, rede, semFuncao }.
 * extra.ficha_id: a ficha SEM conta que o professor ja' tinha ("e' o Marcus — juntar"; fechamento v144).
 */
export async function completarPedidoDoAluno(db, pedido, entrada, extra = {}) {
  const d = normalizar(entrada);
  const c = calcular(d);
  const pendencias = faltando(d);
  if (pendencias.length > 0) return { ok: false, rede: false, erro: pendencias[0].pergunta };
  if (!dataRazoavel(d.data_inicio) || !dataRazoavel(c.data_fim)) {
    return { ok: false, rede: false, erro: 'Confira a quantidade de aulas e a data de início — a conta do fim do pacote não fechou.' };
  }
  // Fechamento v144: "ja' comecamos em 14/10, nenhuma feita" (e o mensal com inicio no passado) — as aulas marcadas
  // comecam HOJE, nao na data de inicio (o banco recusa aula marcada no passado). O acordo guarda o inicio que disseram.
  const hojeStr = fmtData(new Date());
  const dAulas = (d.aulas_feitas > 0 || d.data_inicio >= hojeStr) ? d : { ...d, data_inicio: hojeStr };
  // v155: juntar com ficha que ja' tem aulas — dia+horario ocupado nao ganha outra (a trava do banco recusaria tudo)
  const ocupadas = new Set();
  if (extra && extra.ficha_id) {
    try {
      const { data: jaTem } = await db.from('aulas').select('data, hora').eq('aluno_id', String(extra.ficha_id))
        .in('status', ['agendada', 'aguardando_confirmacao', 'realizada', 'falta']).gte('data', fmtData(new Date(Date.now() - 400 * 86400000)));
      (jaTem || []).forEach(x => ocupadas.add(x.data + '|' + String(x.hora || '').slice(0, 5)));
    } catch (e) { /* sem a lista o banco ainda pula o repetido */ }
  }
  const aulas = gerarAulas(dAulas, dAulas === d ? c : calcular(dAulas), { aluno_id: null, contrato_id: null }, new Set(), ocupadas);
  // pacote: a previsao de renovacao e' a ULTIMA aula da fila (o mesmo acerto do caminho de sempre)
  const ultima = d.tipo_cobranca !== 'mensal' ? aulas.map(x => x.data).sort().pop() : null;
  const dados = {
    nome: d.nome, telefone: d.telefone, modalidade: d.modalidade,
    contrato: { ...linhaContrato(d, c), data_fim: ultima || c.data_fim },
    agenda: d.dias.map(dia => ({ dia_semana: dia, hora: horaDoDia(d, dia) })),
    aulas: aulas.map(a => ({ data: a.data, hora: a.hora, status: a.status })),
    pagamento: linhaPagamentoInicial(d, c, entrada).linha,
    ...(extra && extra.ficha_id ? { ficha_id: String(extra.ficha_id) } : {}),
  };
  let data = null, error = null;
  try { ({ data, error } = await db.rpc('professor_completar_pedido', { p_pedido: pedido, p_dados: dados })); } catch (e) { error = e; }
  if (error) {
    const msg = String((error && error.message) || error);
    // So' "a funcao nao existe" (o SQL da v144 ainda nao rodou) volta ao caminho de antes. Qualquer outro erro de
    // dentro da funcao e' erro: cair no caminho antigo faria ficha nova + convite de volta (o defeito de 21/10).
    const cod = String((error && error.code) || '');
    return { ok: false, rede: true, erro: msg, semFuncao: cod === 'PGRST202' || (cod === '42883' && /professor_completar_pedido/.test(msg)) };
  }
  if (!data || data.ok === false) return { ok: false, codigo: String((data && data.erro) || 'sem_resposta'), motivo: data && data.motivo ? String(data.motivo) : '' };
  // v155: o banco pula aula repetida (a ficha ja' tinha aula no mesmo dia e horario) e diz quantas
  const puladas = Math.max(0, Number(data.aulas_puladas) || 0);
  return { ...data, ok: true, aulas_criadas: Math.max(0, aulas.length - puladas), aulas_puladas: puladas };
}

/**
 * Cria (ou apenas propõe) um aluno com contrato completo.
 * @param {object} db      cliente do banco — navegador hoje, servidor amanhã
 * @param {object} entrada dados crus (da tela ou da voz)
 * @param {object} opcoes  { modo: 'rascunho' | 'aplicar' }
 */
export async function criarAlunoComContrato(db, entrada, opcoes = {}) {
  const modo = opcoes.modo || 'aplicar';
  const d = normalizar(entrada);
  const c = calcular(d);
  const pendencias = faltando(d);

  if (modo === 'rascunho') {
    return {
      ok: pendencias.length === 0,
      proposta_id: `p_${Date.now().toString(36)}`,
      resumo: resumo(d, c),
      faltando: pendencias,
      avisos: await avisos(db, d),
      calculado: c,
      dados: d,
    };
  }

  if (pendencias.length > 0) {
    return { ok: false, erro: pendencias[0].pergunta, faltando: pendencias };
  }

  // Pacote novo para aluno que ja' existe (ex.: "Pacote terminou — renovar"): nao cria
  // aluno nem agenda do zero, so' encerra o contrato antigo e comeca o proximo. Sem isto,
  // a unica forma de renovar caia no assistente antigo — que nao tem "ja' fez quantas
  // aulas" nem "ja' pagou", entao nao dava pra registrar um pacote que ja' comecou ou
  // ja' foi pago (relato do Cadu em 23/09, aluno "Marcos").
  // Ultima trava antes de gravar (v133): data invalida aqui vira "NaN-NaN-NaN" no banco, uma
  // mensagem que ninguem entende. Melhor parar com uma frase de gente — e sem criar aluno orfao,
  // por isso vem ANTES de inserir o aluno.
  if (!dataRazoavel(d.data_inicio) || !dataRazoavel(c.data_fim)) {
    return { ok: false, erro: 'Confira a quantidade de aulas e a data de início — a conta do fim do pacote não fechou.' };
  }

  let aluno;
  let antigos = [];   // v155: acordos individuais encerrados nesta renovacao (sem fila)
  if (d.depois_do_atual) {
    // v149: o pacote atual NAO encerra — ele termina as aulas dele; o banco (_virar_pacote, robo de 10 min)
    // poe o da fila no lugar quando a ultima aula marcada for resolvida. So' um na fila por aluno.
    aluno = { id: d.aluno_id_existente };
    const { data: naFila } = await db.from('contratos').select('id').eq('aluno_id', aluno.id)
      .eq('professor_id', d.professor_id).eq('status', 'proximo').limit(1);
    if (naFila && naFila.length) return { ok: false, erro: 'Este aluno já tem um próximo pacote combinado.' };
  } else if (d.aluno_id_existente) {
    aluno = { id: d.aluno_id_existente };
    // v155: so' o acordo individual (sem turma) encerra — a mensalidade da turma do aluno segue valendo.
    const semRede = { ok: false, erro: 'Não consegui conferir o pacote atual. Confira a internet e tente de novo.' };
    const { data: atuais, error: eAtuais } = await db.from('contratos').select('id, tipo_cobranca')
      .eq('aluno_id', aluno.id).eq('professor_id', d.professor_id).eq('status', 'ativo').is('turma_id', null);
    if (eAtuais) return semRede;
    antigos = atuais || [];
    // v155: pacote com aula ainda marcada nao encerra no meio (aula paga sumia ou ficava presa no acordo encerrado).
    // Aula ja' registrada pelo professor (esperando o aluno) ou em divergencia nao conta: ela fica no pacote atual.
    const idsPacote = antigos.filter(k => k.tipo_cobranca !== 'mensal').map(k => k.id);
    if (idsPacote.length) {
      // v155: "ainda vai acontecer" olha a hora (a aula de hoje que ja' passou nao conta); a que passou sem registro
      // tambem segura (senao ficava presa no acordo encerrado, aula paga sem fechar)
      const { data: marcadas, error: eMarc } = await db.from('aulas').select('data, hora, desfecho_prof, divergencia_em')
        .in('contrato_id', idsPacote).in('status', ['agendada', 'aguardando_confirmacao'])
        .order('data').order('hora');
      if (eMarc) return semRede;
      const ag = agoraSP();
      const semRegistro = (marcadas || []).filter(x => !x.desfecho_prof && !x.divergencia_em);
      const vaiAcontecer = x => x.data > ag.data || (x.data === ag.data && String(x.hora || '').slice(0, 5) > ag.hora);
      const abertas = semRegistro.filter(vaiAcontecer);
      const passadas = semRegistro.filter(x => !vaiAcontecer(x));
      if (abertas.length) {
        const n = abertas.length;
        return { ok: false, codigo: 'pacote_com_aulas', erro: `O pacote atual ainda tem ${n} aula${n === 1 ? '' : 's'} marcada${n === 1 ? '' : 's'}, até ${diaMes(abertas[n - 1].data)}. `
          + 'Termine essas aulas (ou desmarque as que não vão acontecer) antes de começar o acordo novo. Assim nenhuma aula já paga se perde.' };
      }
      if (passadas.length) {
        const n = passadas.length;
        return { ok: false, codigo: 'pacote_aula_sem_registro', erro: `O pacote atual tem ${n} aula${n === 1 ? '' : 's'} que já ${n === 1 ? 'passou' : 'passaram'} sem registro (a última em ${diaMes(passadas[n - 1].data)}). `
          + 'Registre na Agenda se aconteceu ou não antes de começar o acordo novo.' };
      }
    }
    if (antigos.length) {
      const { error: eEncerra } = await db.from('contratos').update({ status: 'encerrado' }).in('id', antigos.map(k => k.id));
      if (eEncerra) return { ok: false, erro: 'Não foi possível encerrar o pacote anterior: ' + eEncerra.message };
    }
  } else {
    const { data: alunoNovo, error: eAluno } = await db.from('alunos').insert({
      professor_id: d.professor_id, nome: d.nome, telefone: d.telefone, email: d.email,
      modalidade: d.modalidade, tipo_vinculo: 'vinculado', observacoes: '',
    }).select().single();
    if (eAluno) return { ok: false, erro: 'Não foi possível criar o aluno: ' + eAluno.message };
    aluno = alunoNovo;
  }

  let { data: contrato, error: eContrato } = await db.from('contratos').insert({
    aluno_id: aluno.id, professor_id: d.professor_id, ...linhaContrato(d, c),
  }).select().single();
  // v144: banco ainda sem a coluna do "desmarcar livre" (o SQL da v144 nao rodou) — grava como antes, sem ela
  if (eContrato && /cancelamento_livre/.test(String(eContrato.message || '')) && 'cancelamento_livre' in linhaContrato(d, c)) {
    const { cancelamento_livre, ...semLivre } = linhaContrato(d, c);
    ({ data: contrato, error: eContrato } = await db.from('contratos').insert({
      aluno_id: aluno.id, professor_id: d.professor_id, ...semLivre,
    }).select().single());
  }
  if (eContrato) return { ok: false, erro: 'Não foi possível criar o contrato: ' + eContrato.message, aluno_id: aluno.id };

  const problemas = [];

  // v155: mensalidade encerrada nesta renovacao — as aulas dela que ainda nao aconteceram saem da agenda (sem saldo a
  // perder: o mensal nao tem pacote). Antes ficavam presas no acordo encerrado, sem lembrete nem "Aconteceu?", e o
  // acordo novo pulava esses horarios. Registrada pelo professor ou em divergencia fica.
  const idsMensal = antigos.filter(k => k.tipo_cobranca === 'mensal').map(k => k.id);
  if (idsMensal.length) {
    const ag = agoraSP();
    const { data: fut, error: eFut } = await db.from('aulas').select('id, data, hora, desfecho_prof, divergencia_em')
      .in('contrato_id', idsMensal).eq('status', 'agendada').gte('data', ag.data);
    // v155: trocou mensal por pacote: as aulas do mes ja' pago ficam (o aluno pagou por elas) e o pacote novo comeca
    // nos horarios livres depois delas (elas entram nas 'ocupadas' abaixo). Mensal por mensal segue como antes.
    let mesesPagos = new Set();
    if (d.tipo_cobranca !== 'mensal') {
      const { data: pagosM } = await db.from('pagamentos').select('referencia_mes')
        .in('contrato_id', idsMensal).eq('status', 'pago');
      mesesPagos = new Set((pagosM || []).map(x => String(x.referencia_mes || '').slice(0, 7)).filter(Boolean));
    }
    const sair = (fut || []).filter(x => !x.desfecho_prof && !x.divergencia_em && (x.data > ag.data || String(x.hora || '').slice(0, 5) > ag.hora)
      && !mesesPagos.has(String(x.data).slice(0, 7))).map(x => x.id);
    if (eFut) problemas.push('aulas do acordo antigo: ' + eFut.message);
    else if (sair.length) {
      const { error: eSai } = await db.from('aulas').delete().in('id', sair);
      if (eSai) problemas.push('aulas do acordo antigo: ' + eSai.message);
    }
  }

  // Renovacao pode trazer dias/horario diferentes do pacote anterior — desativa a
  // agenda antiga antes de gravar a nova (o aluno novo nunca tem agenda pra desativar).
  const trocarAgenda = async () => {
    if (d.aluno_id_existente) {
      // v155: os horarios da turma (turma_id) ficam — so' troca a agenda individual
      await db.from('agenda_semanal').update({ ativo: false }).eq('aluno_id', aluno.id).is('turma_id', null);
    }
    const { error: eAgenda } = await db.from('agenda_semanal').insert(
      d.dias.map(dia => ({ aluno_id: aluno.id, professor_id: d.professor_id, dia_semana: dia, hora: horaDoDia(d, dia), ativo: true }))
    );
    if (eAgenda) problemas.push('agenda: ' + eAgenda.message);
  };
  // v149: na fila a agenda semanal nao muda aqui — o pacote atual segue nos dias dele ate' acabar; a virada no banco
  // (_virar_pacote) grava a agenda do pacote novo a partir das aulas dele.
  if (!d.depois_do_atual) await trocarAgenda();

  // Feriados que valem para este professor. Se ele da' aula em feriado, a lista vem vazia.
  // Feriado NAO some da agenda (Marcus/Cadu, 25/09): muito professor da' aula em feriado.
  // A aula nasce normalmente; o app avisa professor e aluno quando o feriado se aproxima
  // e eles decidem se remarcam. O sistema nao decide por eles.
  const feriados = new Set();
  const ocupadas = new Set();
  if (d.aluno_id_existente) {
    const { data: jaTem } = await db.from('aulas').select('data, hora').eq('aluno_id', aluno.id)
      .in('status', ['agendada', 'aguardando_confirmacao', 'realizada', 'falta']).gte('data', fmtData(new Date(Date.now() - 400 * 86400000)));
    (jaTem || []).forEach(x => ocupadas.add(x.data + '|' + String(x.hora || '').slice(0, 5)));
  }
  const aulas = gerarAulas(d, c, { aluno_id: aluno.id, contrato_id: contrato.id }, feriados, ocupadas);
  if (aulas.length) {
    const { error: eAulas } = await db.from('aulas').insert(aulas);
    if (eAulas && d.depois_do_atual) {
      // Na fila sem aulas, o banco poria um pacote vazio no lugar do atual: desfaz e avisa.
      await db.from('contratos').delete().eq('id', contrato.id);
      return { ok: false, erro: 'Não foi possível marcar as aulas do próximo pacote: ' + eAulas.message };
    }
    if (eAulas) problemas.push('aulas: ' + eAulas.message);
    // (na fila a agenda semanal NAO troca agora: o pacote atual segue nos dias dele; o banco troca na virada)
    // Pacote: a previsao de renovacao e' a ULTIMA aula da fila (regra da fila, 23/09).
    // O calculo acima tem uma semana de folga para feriado; sem este acerto o contrato
    // nascia com data_fim 1 semana depois da ultima aula e, ao desmarcar, a "renovacao"
    // parecia andar para tras (30/10 -> 22/10, Marcus 25/09).
    if (!eAulas && d.tipo_cobranca !== 'mensal') {
      const ultima = aulas.map(x => x.data).sort().pop();
      if (ultima) {
        await db.from('contratos').update({ data_fim: ultima }).eq('id', contrato.id);
        contrato.data_fim = ultima;
      }
    }
  }

  const pg = linhaPagamentoInicial(d, c, entrada);
  if (d.depois_do_atual && d.tipo_cobranca !== 'mensal') {
    // v152: pacote na fila — a cobranca vence no dia da ultima aula do pacote ATUAL (vencimento_fila); sem essa data,
    // ou se ela cair depois do inicio do novo, vence no dia da 1a aula dele. Antes disso e' "a vencer", nunca atrasada.
    // Ja' pago: e' dinheiro de verdade do pacote novo (nao 'migracao', que fica fora do faturamento do mes).
    const datas = aulas.map(x => x.data).sort();
    const ini = datas[0] || d.data_inicio, fimP = datas[datas.length - 1] || c.data_fim;
    const venc = d.vencimento_fila && d.vencimento_fila <= ini ? d.vencimento_fila : ini;
    pg.linha = { ...pg.linha, data_vencimento: venc, periodo_inicio: ini, periodo_fim: fimP, referencia_mes: ini.slice(0, 7) };
  }
  // v155: pacote novo ja' pago (fila ou renovacao comum) e' dinheiro de verdade do pacote novo — nao 'migracao'
  // ("pago antes do cadastro"). Sem data informada fica em branco (o Financeiro pede "confirme a data"); nunca hoje
  // no chute. O mes do recebido e' o do pagamento (sem data: o de hoje, quando o professor disse que recebeu).
  if (d.tipo_cobranca !== 'mensal' && (d.depois_do_atual || d.renovacao) && pg.linha.status === 'pago') {
    pg.linha = { ...pg.linha, tipo: 'aguardando', confirmado_via: null,
      data_pagamento: d.data_pagamento ? d.data_pagamento + 'T12:00:00' : null,
      referencia_mes: (d.data_pagamento || fmtData(new Date())).slice(0, 7) };   // v155: sem data, o mes de hoje (fila ou nao)
  }
  const { error: ePag } = await db.from('pagamentos').insert({
    aluno_id: aluno.id, professor_id: d.professor_id, contrato_id: contrato.id, ...pg.linha,
  });
  if (ePag) problemas.push(pg.rotulo + ': ' + ePag.message);

  // Creditos so' existem no pacote. No mensal nao ha' saldo a controlar — o aluno paga
  // o mes, treinando ou nao. Criar um registro de credito aqui faria as telas mostrarem
  // "restam X aulas" para quem nao tem aulas restantes.
  if (d.tipo_cobranca !== 'mensal' && !d.depois_do_atual) {   // na fila: o saldo continua sendo o do pacote atual
    const cred = await recalcularCreditos(db, aluno.id);
    if (cred && cred.error) problemas.push('créditos: ' + cred.error);
  }

  return {
    ok: true,
    aluno_id: aluno.id,
    contrato_id: contrato.id,
    aulas_criadas: aulas.length,
    cobranca_gerada: c.gera_cobranca ? c.preco_pacote : 0,
    resumo: resumo(d, c),
    problemas,
  };
}
